/* One integration's own page: what it is doing, who may use the assistant through it, and how
 * it is configured. The order is the order the questions get asked — is it working, who may
 * use it, and only then the settings.
 *
 * The traffic is this INTEGRATION's: a turn records which of a product's applications it came
 * through, so a product reached from a web app and a staff portal reads as two. Turns written
 * before that was recorded fall to the product's earliest registration, which is where they
 * belong — until a second one could be added, there was nowhere else they could have come
 * from. See `by_integration` in `queries/adoption.ts`.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, ConfirmModal, Input, Textarea } from '@nextleap-al/admin-ui';
import { ArrowLeft } from 'lucide-react';

import { detailOf, write } from '../api/client';
import type { Adoption, Integration, IntegrationUser } from '../api/types';
import { KeyBox, type IssuedKey } from '../components/KeyBox';
import { Async, PanelCard, PanelError, ResultLine } from '../components/Panel';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num, plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';

/**
 * What this integration is doing, over the period in the toolbar. Scoped by `telemetry_system`
 * rather than the registered key — NextPass writes its rows as `unipass`, and the server
 * decides that translation, see `canonicalSystem` — then narrowed to this registration's own
 * line, which is the figure the card on the index shows too.
 */
function Traffic({ item }: { item: Integration }) {
  const { days } = useApp();
  const query = useApi<Adoption>('/api/adoption', { days, system: item.telemetry_system });

  return (
    <Async query={query}>
      {(data) => {
        const row = data.by_integration.find((r) => r.integration_id === item.id);
        const tiles: TileSpec[] = [
          {
            label: 'Questions',
            value: num(row?.turns ?? 0),
            hint: `turns asked through ${item.display_name} in the period above`,
          },
          {
            label: 'Conversations',
            value: num(row?.conversations ?? 0),
            hint: 'separate sessions, not messages',
          },
          {
            label: 'People we can name',
            value: num(row?.users ?? 0),
            hint: 'turns without a login cannot be counted as people',
          },
        ];

        // The tiles are shown either way, reading nought. A registration nothing has come
        // through yet is the ordinary state of a new one, and hiding its figures made the
        // page answer a different question from the card that links to it, which says 0.
        // The sentence below them says WHY it is nought, which no tile can.
        return (
          <div className="space-y-3">
            <TileRow tiles={tiles} />
            {!row && (
              <p className="max-w-prose text-sm text-[var(--text-muted)]">
                {item.last_seen_at
                  ? `Nothing through ${item.display_name} in this period, though a token from it verified on ${shortDate(item.last_seen_at)}. Widen the period in the toolbar.`
                  : `No token from ${item.display_name} has ever verified, which is almost always a key that has not been pasted into that application.`}
              </p>
            )}
          </div>
        );
      }}
    </Async>
  );
}

/**
 * Who may use the assistant through this integration at all. The registration answers whether
 * an application may talk to the assistant; this answers whether a given person may.
 *
 * The list belongs to this integration and not to its system: the assistant knows exactly
 * which registration minted a token — the host presented this row's API key to get it — and
 * carries the id in the token, so a staff portal and a student app on one product can admit
 * different people.
 */
function AllowList({ item }: { item: Integration }) {
  const { reload } = useApp();
  const query = useApi<{ items: IntegrationUser[] }>(`/api/integrations/${item.id}/users`);
  const [emails, setEmails] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { ok, data } = await write<{ added?: string[]; already?: string[] }>(
      `/api/integrations/${item.id}/users`,
      { emails },
    );
    setBusy(false);
    const added = data.added?.length ?? 0;
    const already = data.already?.length ?? 0;
    setResult({
      ok,
      text: ok
        ? // Both halves, always: "6 added" beside a list of nine reads as three lost rows.
          `${added} added${already ? `, ${already} already on the list` : ''}.`
        : detailOf(data, 'They could not be added.'),
    });
    if (ok) {
      setEmails('');
      reload();
    }
  }

  async function drop(person: IntegrationUser) {
    const { ok, data } = await write(`/api/integrations/${item.id}/users/${person.id}`, {}, 'DELETE');
    setResult({
      ok,
      text: ok ? `${person.email} removed.` : detailOf(data, 'That did not work.'),
    });
    if (ok) reload();
  }

  async function setRestricted(on: boolean) {
    const { ok, data } = await write(`/api/integrations/${item.id}`, {
      restrict_users: on ? 'true' : 'false',
    });
    setResult({
      ok,
      text: ok
        ? on
          ? `Only the people on this list may use the assistant through ${item.display_name}.`
          : `Everybody ${item.display_name} signs in may use the assistant again.`
        : detailOf(data, 'That did not work.'),
    });
    if (ok) reload();
  }

  return (
    <PanelCard
      title="Who may use it"
      hint={
        item.restrict_users ? (
          <>
            <b>Only the people below.</b> Everybody else in {item.display_name} is refused.
          </>
        ) : (
          <>
            <b>Everybody {item.display_name} signs in.</b> The list below is kept but not enforced.
          </>
        )
      }
      action={
        <Button
          variant={item.restrict_users ? 'ghost' : 'outline'}
          size="sm"
          onClick={() => (item.restrict_users ? void setRestricted(false) : setConfirm(true))}
        >
          {item.restrict_users ? 'Let everybody in again' : 'Restrict to this list'}
        </Button>
      }
    >
      <div className="space-y-4">
        <Async query={query}>
          {(data) =>
            data.items.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">Nobody is on this list yet.</p>
            ) : (
              <ul className="divide-y divide-[var(--border-light)] rounded-[var(--radius-md)] border border-[var(--border-light)]">
                {data.items.map((person) => (
                  <li key={person.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">
                      {person.email}
                    </span>
                    <span className="hidden flex-none text-xs text-[var(--text-muted)] sm:block">
                      {person.added_by ? `added by ${person.added_by}` : 'added'}{' '}
                      {shortDate(person.added_at)}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => void drop(person)}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )
          }
        </Async>

        <form onSubmit={add} className="space-y-2">
          <Textarea
            label="Add people, by e-mail"
            rows={2}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={'ana@example.com\nerion@example.com'}
            hint="One per line, or separated by commas. It must be the address that application puts in the token — a different spelling is a different person as far as this list is concerned."
          />
          <Button type="submit" size="sm" isLoading={busy} disabled={!emails.trim()}>
            Add
          </Button>
        </form>

        <p className="max-w-prose text-sm text-[var(--text-muted)]">
          A change here reaches the assistant within 30 seconds and applies to sessions already
          open, not only to the next sign-in.
        </p>

        {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
      </div>

      <ConfirmModal
        isOpen={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={() => {
          setConfirm(false);
          void setRestricted(true);
        }}
        title={`Restrict "${item.display_name}" to ${plural(item.allowed_users, 'person', 'people')}?`}
        message="Everybody else using that application loses the assistant within 30 seconds, mid-conversation if they are in one. They are told to ask an administrator, not that something broke. Other integrations on the same system are unaffected — each has its own list."
        variant="warning"
        confirmLabel="Restrict"
      />
    </PanelCard>
  );
}

function Settings({ item, onIssued }: { item: Integration; onIssued: (k: IssuedKey) => void }) {
  const { reload } = useApp();
  const [displayName, setDisplayName] = useState(item.display_name);
  const [origins, setOrigins] = useState(item.origins.join('\n'));
  const [confirm, setConfirm] = useState<'off' | 'rotate' | 'delete' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const run = async (promise: ReturnType<typeof write>, done: string) => {
    const { ok, data } = await promise;
    setResult({ ok, text: ok ? done : detailOf(data, 'That did not work.') });
    if (ok) reload();
    return { ok, data };
  };

  async function setActive(active: boolean) {
    await run(
      write(`/api/integrations/${item.id}`, { active: active ? 'true' : 'false' }),
      `${item.display_name} turned ${active ? 'on' : 'off'}.`,
    );
  }

  async function rotate() {
    const { ok, data } = await run(
      write<{ api_key?: string }>(`/api/integrations/${item.id}/rotate`),
      `${item.display_name}: new key generated.`,
    );
    const apiKey = (data as { api_key?: string }).api_key;
    if (ok && apiKey) onIssued({ systemKey: item.display_name, value: apiKey, rotated: true });
  }

  return (
    <PanelCard
      title="Settings"
      hint="What this integration is called here, where it may be embedded, and how to cut it off."
    >
      <div className="space-y-4">
        <Input
          label="Integration name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          hint="The application, not the product — the product's own name is set beside the picker on the Documents page."
        />
        <Textarea
          label="Origins it may embed from"
          rows={3}
          value={origins}
          onChange={(e) => setOrigins(e.target.value)}
          hint="Scheme, host and port — no path. One per line. These are the pages allowed to put the assistant in an iframe and hand it a token."
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            run(
              write(`/api/integrations/${item.id}`, {
                display_name: displayName,
                origins,
              }),
              `${item.display_name} updated.`,
            )
          }
        >
          Save changes
        </Button>

        <div className="border-t border-[var(--border-light)] pt-4">
          <p className="max-w-prose text-sm text-[var(--text-secondary)]">
            This integration's key starts <code>{item.key_prefix}…</code>
            {item.rotated_at ? `, last rotated ${shortDate(item.rotated_at)}.` : '.'} The rest of
            it was shown once, when it was generated. It speaks for{' '}
            <Link to="/documents" className="text-[var(--primary)] underline">
              {item.system_label}
            </Link>
            , and that cannot be changed here: <code>{item.system_key}</code> is the{' '}
            <code>iss</code> inside tokens already in flight. Register a second integration if
            another application needs its own key.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (item.active ? setConfirm('off') : setActive(true))}
            >
              {item.active ? 'Turn off' : 'Turn on'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirm('rotate')}>
              Rotate key
            </Button>
            <Button variant="danger" size="sm" onClick={() => setConfirm('delete')}>
              Delete
            </Button>
          </div>
        </div>

        {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
      </div>

      <ConfirmModal
        isOpen={confirm === 'off'}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void setActive(false);
        }}
        title={`Turn off "${item.display_name}"?`}
        message="Everybody using that application loses the assistant within 30 seconds, mid-conversation, not only at their next sign-in — a token it has already issued stops verifying too. Other integrations on the same system keep working, and turning it back on restores it just as quickly."
        variant="warning"
        confirmLabel="Turn off"
      />
      {/* Rotation has no overlap window: the old key stops verifying the moment the new one
          is generated, so the panel says so before it asks. */}
      <ConfirmModal
        isOpen={confirm === 'rotate'}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void rotate();
        }}
        title={`Rotate the key for "${item.display_name}"?`}
        message='The old key stops working immediately. Everyone using that application sees "session could not be verified" until the new key is pasted into its configuration.'
        variant="warning"
        confirmLabel="Rotate"
      />
      <ConfirmModal
        isOpen={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void run(
            write(`/api/integrations/${item.id}`, {}, 'DELETE'),
            `${item.display_name} deleted.`,
          );
        }}
        title={`Delete "${item.display_name}" permanently?`}
        message="Turning it off instead is reversible and keeps the record of an application that once existed beside the analytics its users wrote. Everyone on its list of allowed people is deleted with it. The system it speaks for is untouched."
        variant="danger"
        confirmLabel="Delete"
      />
    </PanelCard>
  );
}

export function IntegrationDetailPanel() {
  const { id } = useParams();
  const query = useApi<{ item: Integration }>(`/api/integrations/${id}`);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  // A deleted integration leaves its page on screen with a 404 behind it. Said as what it is,
  // with the way back, rather than as a panel that failed to load.
  if (query.error) {
    return /→ 404$/.test(query.error.message) ? (
      <div className="space-y-4">
        <BackLink />
        <p className="max-w-prose text-sm text-[var(--text-muted)]">
          That integration is not registered. It may have been deleted from another tab.
        </p>
      </div>
    ) : (
      <PanelError error={query.error} />
    );
  }

  return (
    <div className="space-y-5">
      <BackLink />
      <Async query={query} shape="page">
        {({ item }) => (
          <div className="space-y-5">
            {/* The name alone. There was a row of badges here — the key, active/off, who may sign
                in, the origins — and every one of them is stated below in the card that also lets
                you change it: the key and the origins under Settings, the access rule as the whole
                point of "Who may use it", and on/off as the label of the button that toggles it. A
                summary of the page at the top of the page is one more thing to read and one more
                thing to disagree with what it summarises. */}
            {/* The name, and under it the product it speaks for — the one fact that is NOT
                repeated in a card below, and without which the traffic tiles read as this
                integration's rather than its system's. */}
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">
                {item.display_name}
              </h2>
              <p className="mt-0.5 text-sm text-[var(--text-muted)]">
                An integration on {item.system_label}
              </p>
            </div>

            {issued && <KeyBox issued={issued} onDismiss={() => setIssued(null)} />}

            <Traffic item={item} />

            <AllowList item={item} />
            <Settings item={item} onIssued={setIssued} />
          </div>
        )}
      </Async>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/integrations"
      className="inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      All integrations
    </Link>
  );
}
